/* eslint-disable @nice-cxone/ccf/required-tsdoc */

import { Avatar } from '@mui/material';
import {
  CcfPopOver,
  CcfTwitterAuthBatchIcon,
  CcfTwitterCommentsIcon,
  CcfTwitterFavoriteIcon,
  CcfTwitterRetweetIcon,
  CcfTypography,
  CcfTwitterDeleteIcon,
  CcfButton,
  CcfFacebookLikeIcon,
  CcfFacebookLoveIcon,
  CcfFacebookApproveIcon,
  CcfFacebookCommentIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { Contact } from '../../ccf-inbox/model/contact';
import { ClassNameMap } from '@mui/styles';
import { PopOverMenuItem } from '../../ccf-inbox/model/callControlPopover';
import {
  fomatToDateString,
  fomatToTimeString,
  InteractionTabType,
} from '@nice-ccf/core-sdk';
import styles from './ccf-digital-contact-body.module.scss';
import {
  DigitalInteractionDataType,
  DigitalChannelMessage,
} from './ccf-digital-interaction-module-type';

interface inteactionType {
  facebook?: boolean;
  twitter?: boolean;
  instagram?: boolean;
}

/**
 * Component to display Twitter card
 * @param selectedCallContact - Contact
 * @param miniCard - boolean
 * @param replyCard - replyCardType
 * @param hasReply - boolean
 * @param handleReplyClick -
 * @param handleDeleteClick - string
 * @param classes - ClassNameMap
 * @example <CcfDigitalMessageCard />
 * @returns twitter card
 */
export function CcfDigitalMessageCard(
  selectedCallContact: Contact,
  interactionData: DigitalInteractionDataType,
  miniCard: boolean,
  replyCard: DigitalChannelMessage,
  hasReply?: boolean,
  handleReplyClick?: () => void,
  handleDeleteClick?: (id?: string) => void,
  classes?: ClassNameMap
) {
  const { hasAbilityToLike, hasAbilityToShare, realExternalPlatformId } =
    interactionData as DigitalInteractionDataType;
  const isTwitterButtonsDisabled = selectedCallContact?.contactStatus === 'new';
  const currentInteactionType: inteactionType = {};
  switch (realExternalPlatformId) {
    case 'fb':
      currentInteactionType.facebook = true;
      break;
    case 'ig':
      currentInteactionType.instagram = true;
      break;
    case 'tw':
      currentInteactionType.twitter = true;
      break;
    default:
      break;
  }
  const [translate] = useTranslator();
  const dropdownOptions = {
    menuItems: [
      {
        items: [
          {
            label: translate('deleteTweet'),
            icon: <CcfTwitterDeleteIcon />,
            closeOnSelection: true,
          },
        ],
      },
    ],
  };

  /**
   * Function for oppover item selection
   * @param item - PopOverMenuItem
   * @example - onPopOverItemSelection(item)
   * @returns
   */
  const onPopOverItemSelection = (item: PopOverMenuItem) => () => {
    if (item.label === translate('deleteTweet')) {
      handleDeleteClick && handleDeleteClick(replyCard.messageId);
    }
  };
  const showDividerClass =
    hasReply || (miniCard && !currentInteactionType.facebook)
      ? `${styles.divider} ${classes?.divider}`
      : '';

  const isFacebookCommentPost = currentInteactionType.facebook && !miniCard;

  const facebookIcons = () => {
    return (
      <div className={`${styles.facebookIcons}`}>
        <div className={`${styles.iconsContainer}`}>
          <span>
            <CcfFacebookLikeIcon viewBox="-4 -4 24 24" />
          </span>
          <span>
            <CcfFacebookLoveIcon viewBox="-4 -4 24 24" />
          </span>
          <span>24 | 2 Replies</span>
        </div>
      </div>
    );
  };

  const facebookCommentActions = () => {
    return (
      <div className={styles.faceBookCommentActionWrapper}>
        <div>
          <CcfButton
            variant="outlined"
            className={`${styles.twitterFavoriteButton} ${classes?.actionButton}`}
            disabled={isTwitterButtonsDisabled}
            startIcon="🏳️"
          >
            <span className={`${styles.flaggedComment}`}>Flag</span>
          </CcfButton>
        </div>
        {facebookIcons()}
      </div>
    );
  };

  const facebookCommentReply = () => {
    return (
      <>
        <div className={styles.replyBody}>
          <div className={styles.replyContent}>
            <div className={styles.originalTweetTitle}>
              {`${
                replyCard?.authorUser?.firstName ||
                [
                  selectedCallContact?.customer?.firstName,
                  selectedCallContact?.customer?.middleName,
                  selectedCallContact?.customer?.lastName,
                ]
                  .filter(Boolean)
                  .join(' ')
              } `}
              {!currentInteactionType.facebook ? (
                <CcfTwitterAuthBatchIcon viewBox="-5 -1 24 24" />
              ) : null}{' '}
            </div>
            <div className={styles.originalTweetSubtitle}>
              {'May 24th at 1.06pm'}
            </div>
          </div>
          <div className="cardBody">
            {replyCard.messageContent.payload.text}
          </div>
        </div>
        <img alt="tweet" src={'/assets/Instagram_Post.png'} />
      </>
    );
  };

  return (
    <div
      className={`${styles.twitterCardWrapper} ${showDividerClass} ${
        !hasReply ? classes?.replyCard : ''
      }`}
      // ${
      //   replyCard?.isLastCard && !hasReply ? styles.replyCard : ''
      // }`}
    >
      <div
        className={`
        ${styles.avatarWrapper}
        ${isFacebookCommentPost ? styles.avatarWrapperMargin : ''}`}
      >
        <div>
          <Avatar
            alt="Twitter Reply"
            src="https://mui.com/static/images/avatar/7.jpg"
          />
        </div>
        <div
          className={`${
            currentInteactionType.facebook && !miniCard
              ? styles.leftDividerWrapper
              : ''
          }`}
        ></div>
      </div>
      <div
        className={`${
          miniCard ? styles.twitterMiniCard : styles.twitterCardContainer
        }
          ${styles?.digitalInteractionCardContainer}`}
      >
        {!miniCard && (
          <div className={styles.cardHeader}>
            <div className={styles.facebookCommentWrapper}>
              <div>
                # {selectedCallContact.id}
                {isFacebookCommentPost ? <span role="img">🏴 1</span> : null}
              </div>
              {isFacebookCommentPost ? (
                <div>
                  <CcfTypography translationKey="replyToPreviousComment" />
                </div>
              ) : null}
            </div>
          </div>
        )}
        {currentInteactionType.facebook && !miniCard ? null : (
          <div className="cardTitle">
            <div className="dFlex">
              {miniCard
                ? replyCard?.authorUser.firstName
                : [
                    selectedCallContact?.customer?.firstName,
                    selectedCallContact?.customer?.middleName,
                    selectedCallContact?.customer?.lastName,
                  ]
                    .filter(Boolean)
                    .join(' ')}
              {/* {`${replyCard?.message?.name || selectedCallContact?.customer?.customerName} `} */}
              {miniCard && replyCard.isOwn && (
                <CcfTwitterAuthBatchIcon viewBox="-5 -1 24 24" />
              )}
            </div>
            <span>
              {currentInteactionType.instagram
                ? '14h ago'
                : fomatToTimeString(replyCard.createdAt) +
                  ' • ' +
                  fomatToDateString(replyCard.createdAt)}

              {miniCard && replyCard.isOwn && (
                <CcfPopOver
                  onPopOverItemSelection={onPopOverItemSelection}
                  optionList={dropdownOptions}
                />
              )}
            </span>
          </div>
        )}
        {!miniCard && currentInteactionType.facebook ? (
          <div className={styles.facebookCommentReply}>
            {facebookCommentReply()}
          </div>
        ) : null}
        <div className="cardSubtitle">
          {currentInteactionType.twitter ? (
            <>
              <CcfTypography translationKey="replyingTo" />
              &nbsp;<span>{replyCard?.tweetId}</span>
            </>
          ) : null}
        </div>
        {/* Need to check below logic */}
        {!currentInteactionType.facebook ? (
          <div className="cardBody">
            {replyCard.messageContent.payload.text}
          </div>
        ) : null}
        {currentInteactionType.facebook && miniCard ? (
          <div className="cardBody">
            {replyCard.messageContent.payload.text}
          </div>
        ) : null}
        <div className={styles.cardFooter}>
          <div
            className={`${
              currentInteactionType.facebook ? styles.facebookComment : ''
            }`}
          >
            {!miniCard && (
              <>
                <CcfButton
                  variant="contained"
                  className={`${styles.twitterCommentButton} ${classes?.actionButton}`}
                  // disabled={isTwitterButtonsDisabled || !hasAbilityToQuoteMessage}
                  startIcon={
                    isFacebookCommentPost ? (
                      <CcfFacebookCommentIcon
                        viewBox="0 0 32 32"
                        width={15}
                        height={15}
                        stroke="white"
                        strokeWidth="2"
                      />
                    ) : (
                      <CcfTwitterCommentsIcon viewBox="-4 -4 24 24" />
                    )
                  }
                  onClick={handleReplyClick}
                  id="agentInteractionWorkspace_chat_replyButton"
                >
                  {translate('reply')}
                </CcfButton>
                <CcfButton
                  variant="outlined"
                  className={`${styles.twitterFavoriteButton} ${classes?.actionButton}`}
                  disabled={isTwitterButtonsDisabled || !hasAbilityToLike}
                  startIcon={
                    isFacebookCommentPost ? (
                      <CcfFacebookApproveIcon
                        enableBackground="new 0 0 64 64"
                        viewBox="0 0 50 60"
                        height="30"
                        width="30"
                        classes={{
                          root: styles.twitterFavoriteIconColor,
                        }}
                      />
                    ) : (
                      <CcfTwitterFavoriteIcon
                        viewBox="-4 -4 24 24"
                        classes={{
                          root: styles.twitterFavoriteIconColor,
                        }}
                      />
                    )
                  }
                >
                  {translate('like')}
                </CcfButton>
                {currentInteactionType.twitter ? (
                  <CcfButton
                    variant="outlined"
                    className={`${styles.twitterRetweetButton} ${classes?.actionButton}`}
                    disabled={isTwitterButtonsDisabled || !hasAbilityToShare}
                    startIcon={
                      <CcfTwitterRetweetIcon
                        viewBox="-4 -4 24 24"
                        classes={{
                          root: styles.twitterCommentIconColor,
                        }}
                      />
                    }
                  >
                    {translate('retweet')}
                  </CcfButton>
                ) : null}
              </>
            )}
          </div>
          {currentInteactionType.facebook && miniCard ? (
            facebookCommentActions()
          ) : !currentInteactionType.facebook ? (
            <div className={styles.iconsContainer}>
              <span
                className={
                  !currentInteactionType.twitter ? styles.commentIconOder : ''
                }
              >
                <CcfTwitterCommentsIcon viewBox="-4 -4 24 24" />
                17
              </span>
              {currentInteactionType.twitter ? (
                <span>
                  <CcfTwitterRetweetIcon viewBox="-4 -4 24 24" />2
                </span>
              ) : null}
              <span
                className={
                  !currentInteactionType.twitter
                    ? styles.favouriteIconOrder
                    : ''
                }
              >
                <CcfTwitterFavoriteIcon viewBox="-4 -4 24 24" />
                13
              </span>
            </div>
          ) : (
            facebookIcons()
          )}
        </div>
      </div>
    </div>
  );
}

export default CcfDigitalMessageCard;
